import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Drawer,
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Stack,
  Card,
  Button,
  CircularProgress,
} from '@mui/material';
import {
  Search,
  X,
  Check,
  Server,
  Zap,
  Globe,
  Radio,
} from 'lucide-react';

/**
 * Returns a cohesive color scheme based on driver type without exposing internal engine names
 */
export function getDriverTheme(driverType) {
  const norm = (driverType || '').toLowerCase();
  if (norm === 'adex') {
    return {
      bg: '#EFF6FF',
      text: '#1D4ED8',
      border: '#BFDBFE',
      icon: Globe,
    };
  }
  if (norm === 'msorg') {
    return {
      bg: '#F5F3FF',
      text: '#6D28D9',
      border: '#DDD6FE',
      icon: Server,
    };
  }
  if (norm === 'alisbuild') {
    return {
      bg: '#ECFDF5',
      text: '#047857',
      border: '#A7F3D0',
      icon: Globe,
    };
  }
  if (norm === 'smeplug') {
    return {
      bg: '#FFFBEB',
      text: '#B45309',
      border: '#FDE68A',
      icon: Zap,
    };
  }
  return {
    bg: '#F3F4F6',
    text: '#374151',
    border: '#E5E7EB',
    icon: Globe,
  };
}

export default function VendorSelectModal({
  open,
  onClose,
  processors = [],
  selectedProcessor,
  onSelectProcessor,
  loading = false,
}) {
  const [searchQuery, setSearchQuery] = useState('');

  // Filter and search computation
  const filteredProcessors = useMemo(() => {
    const list = processors || [];
    const query = searchQuery.trim().toLowerCase();
    if (!query) return list;

    return list.filter((p) => {
      const nameMatch = (p.displayName || '').toLowerCase().includes(query);
      const slugMatch = (p.name || '').toLowerCase().includes(query);
      const urlMatch = (p.baseUrl || '').toLowerCase().includes(query);
      const catMatch = Array.isArray(p.supportedCategories)
        ? p.supportedCategories.some((cat) => cat.toLowerCase().includes(query))
        : false;

      return nameMatch || slugMatch || urlMatch || catMatch;
    });
  }, [processors, searchQuery]);

  const handleSelect = (proc) => {
    onSelectProcessor(proc);
    onClose();
  };

  const renderContent = () => {
    if (loading) {
      return (
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8}>
          <CircularProgress size={32} />
          <Typography variant="body2" sx={{ color: '#6B7280', mt: 2 }}>
            Loading vendor catalog...
          </Typography>
        </Box>
      );
    }

    if (filteredProcessors.length === 0) {
      return (
        <Box textAlign="center" py={6} px={2}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              bgcolor: '#E5E7EB',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#6B7280',
              mb: 2,
            }}
          >
            <Search size={24} />
          </Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#111827' }}>
            No vendor templates found
          </Typography>
          <Typography variant="body2" sx={{ color: '#6B7280', mt: 0.5, mb: 2 }}>
            No vendors matched &quot;{searchQuery}&quot;. You can clear your search or select Custom Gateway.
          </Typography>
          <Stack direction="row" spacing={1.5} justifyContent="center">
            {searchQuery && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => setSearchQuery('')}
                sx={{ textTransform: 'none', borderRadius: '10px' }}
              >
                Clear search
              </Button>
            )}
            {processors.find((p) => p.name === 'custom') && (
              <Button
                variant="contained"
                size="small"
                onClick={() => handleSelect(processors.find((p) => p.name === 'custom'))}
                sx={{ textTransform: 'none', borderRadius: '10px' }}
              >
                Use Custom Gateway
              </Button>
            )}
          </Stack>
        </Box>
      );
    }

    return (
      <Stack spacing={1.5}>
        {filteredProcessors.map((proc) => {
          const isSelected =
            selectedProcessor?.name?.toLowerCase() === (proc.name || '').toLowerCase() ||
            (selectedProcessor?.slug && selectedProcessor.slug.toLowerCase() === (proc.name || '').toLowerCase());

          const theme = getDriverTheme(proc.driverType);
          const IconComp = theme.icon;
          const isCustom = proc.name === 'custom' || proc.driverType === 'custom';

          return (
            <Card
              key={proc.name || proc.slug}
              onClick={() => handleSelect(proc)}
              sx={{
                p: 2,
                borderRadius: '16px',
                bgcolor: isSelected ? '#F0FDF4' : '#FFFFFF',
                border: isSelected ? '2px solid #22C55E' : '1px solid #ECEEF1',
                cursor: 'pointer',
                transition: 'all 0.15s ease-in-out',
                boxShadow: isSelected ? '0 4px 12px rgba(34, 197, 94, 0.08)' : '0 1px 3px rgba(0,0,0,0.02)',
                '&:hover': {
                  borderColor: isSelected ? '#22C55E' : '#0066FE',
                  bgcolor: isSelected ? '#F0FDF4' : '#FAFCFF',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 4px 12px rgba(0, 102, 254, 0.06)',
                },
              }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack direction="row" alignItems="center" spacing={2} sx={{ minWidth: 0, flex: 1 }}>
                  {/* Vendor Avatar Icon */}
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: '12px',
                      bgcolor: theme.bg,
                      color: theme.text,
                      border: `1px solid ${theme.border}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <IconComp size={22} />
                  </Box>

                  {/* Vendor Details */}
                  <Box sx={{ minWidth: 0, flex: 1, pr: 1 }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap', mb: 0.25 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 700,
                          color: '#111827',
                          fontSize: '0.95rem',
                          lineHeight: 1.3,
                        }}
                      >
                        {proc.displayName || proc.name}
                      </Typography>
                      {isCustom && (
                        <Chip
                          label="Custom"
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: '0.65rem',
                            fontWeight: 700,
                            bgcolor: '#F3F4F6',
                            color: '#4B5563',
                            borderRadius: '6px',
                          }}
                        />
                      )}
                    </Stack>

                    {/* Base URL or domain */}
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#6B7280',
                        display: 'block',
                        fontFamily: 'monospace',
                        fontSize: '0.75rem',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {proc.baseUrl || 'Custom base URL required'}
                    </Typography>

                    {/* Supported Categories Badges */}
                    {Array.isArray(proc.supportedCategories) && proc.supportedCategories.length > 0 && (
                      <Stack direction="row" spacing={0.5} mt={0.75} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                        {proc.supportedCategories.slice(0, 4).map((cat) => (
                          <Typography
                            key={cat}
                            component="span"
                            sx={{
                              fontSize: '0.675rem',
                              color: '#4B5563',
                              bgcolor: '#F3F4F6',
                              px: 0.75,
                              py: 0.15,
                              borderRadius: '4px',
                              textTransform: 'capitalize',
                            }}
                          >
                            {cat}
                          </Typography>
                        ))}
                        {proc.supportedCategories.length > 4 && (
                          <Typography
                            component="span"
                            sx={{
                              fontSize: '0.675rem',
                              color: '#9CA3AF',
                              px: 0.5,
                              py: 0.15,
                            }}
                          >
                            +{proc.supportedCategories.length - 4} more
                          </Typography>
                        )}
                      </Stack>
                    )}
                  </Box>
                </Stack>

                {/* Radio / Selection Indicator */}
                <Box sx={{ flexShrink: 0, ml: 1 }}>
                  {isSelected ? (
                    <Box
                      sx={{
                        width: 26,
                        height: 26,
                        borderRadius: '50%',
                        bgcolor: '#22C55E',
                        color: '#FFFFFF',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Check size={16} strokeWidth={3} />
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        width: 26,
                        height: 26,
                        borderRadius: '50%',
                        border: '2px solid #E5E7EB',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'transparent',
                        '&:hover': {
                          borderColor: '#0066FE',
                        },
                      }}
                    >
                      <Radio size={14} />
                    </Box>
                  )}
                </Box>
              </Stack>
            </Card>
          );
        })}
      </Stack>
    );
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 500, md: 560 },
          maxWidth: '100vw',
          height: '100%',
          boxShadow: '-8px 0 32px rgba(0,0,0,0.12)',
          display: 'flex',
          flexDirection: 'column',
          borderLeft: (theme) => `1px solid ${theme.palette.divider}`,
        },
      }}
    >
      {/* Drawer Header */}
      <Box
        sx={{
          p: 3,
          pb: 2,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          borderBottom: '1px solid #F3F4F6',
          bgcolor: '#FFFFFF',
          position: 'sticky',
          top: 0,
          zIndex: 10,
        }}
      >
        <div>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Typography variant="h5" sx={{ fontWeight: 800, color: '#111827' }}>
              Choose Vendor
            </Typography>
            <Chip
              label={`${processors.length} available`}
              size="small"
              sx={{
                bgcolor: '#EFF6FF',
                color: '#0066FE',
                fontWeight: 600,
                fontSize: '0.75rem',
                height: 22,
              }}
            />
          </Stack>
          <Typography variant="body2" sx={{ color: '#6B7280', mt: 0.5 }}>
            Select a verified vendor template to pre-configure endpoints and settings.
          </Typography>
        </div>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: '#9CA3AF',
            bgcolor: '#F9FAFB',
            '&:hover': { bgcolor: '#F3F4F6', color: '#111827' },
          }}
        >
          <X size={18} />
        </IconButton>
      </Box>

      {/* Search Bar */}
      <Box sx={{ p: 2.5, bgcolor: '#FFFFFF', borderBottom: '1px solid #F3F4F6' }}>
        <TextField
          fullWidth
          size="small"
          autoFocus
          placeholder="Search by vendor name or domain (e.g. QuicklySim, Datatap, SMEPlug)..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={18} color="#9CA3AF" />
              </InputAdornment>
            ),
            endAdornment: searchQuery ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchQuery('')}>
                  <X size={16} color="#9CA3AF" />
                </IconButton>
              </InputAdornment>
            ) : null,
            sx: {
              borderRadius: '12px',
              bgcolor: '#F9FAFB',
              '& fieldset': { borderColor: '#E5E7EB' },
              '&:hover fieldset': { borderColor: '#D1D5DB' },
              '&.Mui-focused fieldset': { borderColor: '#0066FE' },
            },
          }}
        />
      </Box>

      {/* Vendor List */}
      <Box sx={{ p: 2.5, bgcolor: '#F9FAFB', overflowY: 'auto', flex: 1 }}>
        {renderContent()}
      </Box>
    </Drawer>
  );
}

VendorSelectModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  processors: PropTypes.array,
  selectedProcessor: PropTypes.object,
  onSelectProcessor: PropTypes.func.isRequired,
  loading: PropTypes.bool,
};
